import React, { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { ArrowUpRight, ArrowDownRight, Wrench } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { VInventoryDashboard } from '../../lib/types';
type MovementType = 'restock' | 'waste' | 'manual_adjustment';
interface StockAdjustmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: VInventoryDashboard | null;
  onAdjust: (itemId: string, quantity: number, type: MovementType, reason?: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
}
const MOVEMENT_TYPES: {
  value: MovementType;
  label: string;
  description: string;
  icon: typeof ArrowUpRight;
  color: string;
}[] = [{
  value: 'restock',
  label: 'Reabastecimiento',
  description: 'Agregar stock (compra, recepción)',
  icon: ArrowUpRight,
  color: 'emerald'
}, {
  value: 'waste',
  label: 'Desperdicio / Merma',
  description: 'Reducir stock (desperdicio, vencido)',
  icon: ArrowDownRight,
  color: 'red'
}, {
  value: 'manual_adjustment',
  label: 'Ajuste Manual',
  description: 'Corrección de inventario',
  icon: Wrench,
  color: 'amber'
}];
export function StockAdjustmentModal({
  isOpen,
  onClose,
  item,
  onAdjust
}: StockAdjustmentModalProps) {
  const [movementType, setMovementType] = useState<MovementType>('restock');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (isOpen) {
      setMovementType('restock');
      setQuantity('');
      setReason('');
      setError(null);
    }
  }, [isOpen]);
  if (!item) return null;
  const qty = parseFloat(quantity) || 0;
  const delta = movementType === 'restock' ? qty : -qty;
  const newStock = Math.max(0, item.current_stock + delta);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (qty <= 0) {
      setError('La cantidad debe ser mayor a 0');
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      const result = await onAdjust(item.inventory_item_id, qty, movementType, reason || undefined);
      if (result.success) {
        onClose();
      } else {
        setError(result.error || 'Error al ajustar stock');
      }
    } catch (err: any) {
      setError(err?.message || 'Error inesperado');
    }
    setIsSubmitting(false);
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={`Ajustar Stock: ${item.ingredient_name}`} data-id="element-754">
      <form onSubmit={handleSubmit} className="space-y-5" data-id="element-755">
        {/* Current Stock Display */}
        <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-between" data-id="element-756">
          <div data-id="element-757">
            <p className="text-sm text-gray-500" data-id="element-758">Stock actual</p>
            <p className="text-2xl font-bold text-gray-900" data-id="element-759">
              {item.current_stock}{' '}
              <span className="text-base font-normal text-gray-500" data-id="element-760">
                {item.unit}
              </span>
            </p>
          </div>
          {qty > 0 && <div className="text-right" data-id="element-761">
              <p className="text-sm text-gray-500" data-id="element-762">Nuevo stock</p>
              <p className={cn('text-2xl font-bold', movementType === 'restock' ? 'text-emerald-600' : 'text-red-600')} data-id="element-763">
                {newStock}{' '}
                <span className="text-base font-normal text-gray-500" data-id="element-764">
                  {item.unit}
                </span>
              </p>
            </div>}
        </div>

        {/* Movement Type Selection */}
        <div data-id="element-765">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-766">
            Tipo de movimiento
          </label>
          <div className="grid grid-cols-1 gap-2" data-id="element-767">
            {MOVEMENT_TYPES.map(type => {
            const isSelected = movementType === type.value;
            const Icon = type.icon;
            return <button key={type.value} type="button" onClick={() => setMovementType(type.value)} className={cn('flex items-center gap-3 p-3 rounded-xl border text-left transition-all', isSelected ? type.color === 'emerald' ? 'border-emerald-300 bg-emerald-50 ring-1 ring-emerald-200' : type.color === 'red' ? 'border-red-300 bg-red-50 ring-1 ring-red-200' : 'border-amber-300 bg-amber-50 ring-1 ring-amber-200' : 'border-gray-200 bg-white hover:border-gray-300')} data-id="element-768">
                  <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0', isSelected ? type.color === 'emerald' ? 'bg-emerald-100 text-emerald-600' : type.color === 'red' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-400')} data-id="element-769">
                    <Icon className="w-4 h-4" data-id="element-770" />
                  </div>
                  <div data-id="element-771">
                    <p className={cn('text-sm font-semibold', isSelected ? 'text-gray-900' : 'text-gray-700')} data-id="element-772">
                      {type.label}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-773">{type.description}</p>
                  </div>
                </button>;
          })}
          </div>
        </div>

        {/* Quantity */}
        <Input label={`Cantidad (${item.unit})`} type="number" min="0.01" step="0.01" value={quantity} onChange={e => setQuantity(e.target.value)} required placeholder="Ej: 10" data-id="element-774" />

        {/* Reason */}
        <Input label="Razón (opcional)" value={reason} onChange={e => setReason(e.target.value)} placeholder="Ej: Compra semanal, Producto vencido" data-id="element-775" />

        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3" data-id="element-776">
            {error}
          </div>}

        <div className="pt-2 flex justify-end gap-3" data-id="element-777">
          <Button type="button" variant="ghost" onClick={onClose} data-id="element-778">
            Cancelar
          </Button>
          <Button type="submit" isLoading={isSubmitting} data-id="element-779">
            {movementType === 'restock' ? 'Agregar Stock' : 'Registrar Ajuste'}
          </Button>
        </div>
      </form>
    </Modal>;
}